import { StripHTML } from '@/utils/utils';

const ISSUES = [
  'target_not_global',
  'target_other_client',
  'target_local_but_resource_is_client',
  'target_shares_no_access_group',
  'target_not_found',
  'would_create_cycle',
  'target_not_readable',
] as const;

export type TRuleReferenceIssue = (typeof ISSUES)[number];

/** The backend's body for a save rejected because of a rule's reference to another question. */
export type TRuleReferenceBlockedError = {
  statusCode: number;
  message: string;
  code: 'rule_reference_blocked';
  issue: TRuleReferenceIssue;
  ruleId: string;
  ruleName?: string;
  via: 'jump_to' | 'condition';
  targetQuestionId: string;
  targetVisibility?: 'global' | 'client' | 'local';
};

type TTranslate = (key: string, options?: Record<string, unknown>) => string;

const MAX_QUESTION_TEXT_LENGTH = 80;

const isRuleReferenceBlockedError = (data: unknown): data is TRuleReferenceBlockedError =>
  !!data &&
  typeof data === 'object' &&
  'code' in data &&
  data.code === 'rule_reference_blocked' &&
  'issue' in data &&
  (ISSUES as readonly unknown[]).includes(data.issue);

export function getRuleReferenceBlockedError(e: unknown): TRuleReferenceBlockedError | undefined {
  if (!e || typeof e !== 'object') return undefined;
  if ('error' in e) return getRuleReferenceBlockedError(e.error);
  if ('data' in e && isRuleReferenceBlockedError(e.data)) return e.data;
  return undefined;
}

export type TBlockedRuleReference = Pick<TRuleReferenceBlockedError, 'issue' | 'ruleName' | 'via' | 'targetQuestionId'>;

export function describeRuleReferenceBlockedError(
  error: TBlockedRuleReference,
  t: TTranslate,
  targetQuestionText?: string,
  options: { includeFix?: boolean } = {},
): { title: string; description: string } {
  const { includeFix = true } = options;
  const prefix = 'errors.ruleReferenceBlocked';
  const title = error.ruleName ? t(`${prefix}.title`, { ruleName: error.ruleName }) : t(`${prefix}.titleUnnamed`);
  const target = targetQuestionText
    ? t(`${prefix}.targetText`, { text: targetQuestionText })
    : t(`${prefix}.targetId`, { id: error.targetQuestionId });
  const reference =
    error.via === 'condition' ? t(`${prefix}.readsAnswerOf`, { target }) : t(`${prefix}.jumpsTo`, { target });

  const sentences = [reference, t(`${prefix}.reason.${error.issue}`)];
  if (includeFix) sentences.push(t(`${prefix}.fix`));

  return { title, description: sentences.join(' ') };
}

type TLibraryEntry = { text?: Record<string, string[] | string>; language?: { c?: string } };

export function getLibraryQuestionText(library: Record<string, unknown>, questionId: string): string | undefined {
  const entry = library[questionId] as TLibraryEntry | undefined;
  const language = entry?.language?.c;
  const text = language ? entry?.text?.[language] : undefined;
  if (!text) return undefined;

  const plain = StripHTML(text)?.trim();
  if (!plain) return undefined;
  return plain.length > MAX_QUESTION_TEXT_LENGTH ? `${plain.slice(0, MAX_QUESTION_TEXT_LENGTH - 1)}…` : plain;
}
